<script>
    import { onMount } from "svelte";

    import { allGroups, isMobile } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        Button,
        CollapsibleTableIcons,
        PostCsvFileModal,
        SearchSelect,
        NotificationModal,
        LoadingModal,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
        SendQuoteOrInvoiceToPatientModal,
    } from "../../Components/Common/index.svelte";

    import { user } from "../../stores";

    import { apiMethods } from "../../Services/index.svelte";

    import { estimateStatus } from "../../Utils/utilEnums.svelte";

    import {
        getAmountInCurrency,
        goToURL,
        replaceURL,
        getPaymentAmountPaid,
        getFormatDate,
        getFormatDateAndTime,
        getDisplayTotal,
        showError,
        showException,
        formatPortalUrls,
        goToDownloadPage,
        getEstimateStatus,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        openPortalPage,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import DecimalInput from "../../Components/Common/DecimalInput.svelte";

    import { cacheAllGroups } from "../../Utils/utilCache.svelte";

    import {
        EmptyGroupDetails,
        MaxPaperProcessingFee,
    } from "../../Utils/utilConstants.svelte";

    export let params = {};

    let isGettingEstimates = false,
        returnEstimatesArr = [],
        filters = { status: "1,2,4,5" },
        page = 0,
        pageSize = 25,
        count,
        checkboxString = "",
        selectedEstimates = [],
        selectedEstimatesIdArr = [],
        isPostCSVModalOpen = false,
        isDownloadingEstimate = false,
        isRegeneratingEstimate = false,
        isSetEstimatePaperModalOpen = false,
        selectedEstimateID = Number(0),
        isSettingEstimatePaper = false,
        isSendingEstimateToPatient = false,
        showFilters = false;

    let selectedDoctors = [];

    let sendQuoteToPatientModalOpen = false,
        selectedQuoteData = {};

    $: columns = [
        {
            key: "selected?",
            title: "",
            headerClass: "toggleSelectAllHeader text-center ml-3 rounded-tl-xl",
            renderValue: (v) =>
                `<input type="checkbox" class="checkbox checkbox-primary checkbox-xs ml-3 text-white cursor-pointer" name="selectCheckBox" id="selectCheckBoxId${v.id}">`,
            csvExportHidden: true,
        },
        {
            key: "estimateNumber",
            title: "Quote Number",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getEstimateNumber(v.estimateNumber),
        },
        {
            key: "encounterId",
            title: "Enc. ID",
            headerClass: "text-left px-3",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getEncounterID(v.patientEncounterId),
            csvContent: (v) => _getEncounterID(v.patientEncounterId, true),
        },
        {
            key: "doctorName",
            title: "Doctor Name",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                `<div class="cursor-pointer hover:underline w-[98px]">${
                    v.doctorName ?? "-"
                }</div>`,
            csvContent: (v) => `${v.doctorName}`,
            csvExportHidden: !isUserEnableViewDoctor,
            isHidden: !isUserEnableViewDoctor,
        },
        {
            key: "patientName",
            title: "Patient Name",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getPatientName(v.billingDetails),
        },
        {
            key: "createdDate",
            title: "Create Date",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getDate(v.createdDate, v.isExpired),
        },
        {
            key: "expireDate",
            title: "Due Date",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getDate(v.expireDate, v.isExpired, true),
        },
        {
            key: "quoteApprove",
            title: "Approved Date",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                _getQuoteApproveDate(v.quoteApprovedDate, v.quoteApproved),
        },
        {
            key: "gapFee",
            title: "Gap Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.actualGap),
        },
        {
            key: "rebate",
            title: "Rebate",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.rebate),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) =>
                getAmountInCurrency(
                    v.addGST ? (v.actualGap + v.rebate) * 0.1 : 0,
                ),
        },
        {
            key: "processingFee",
            title: `Processing Fee`,
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.internalProcessingFee ?? 0),
            headerConfig: {
                showTooltip: true,
                tooltipContent: "Internal Processing Fee",
            },
        },
        {
            key: "totalFee",
            title: "Total Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getDisplayTotal(v),
        },
        {
            key: "payments",
            title: "Payments",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getPaymentAmountPaid(v.payments),
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getStatus(v.status),
            csvContent: (v) => _getStatus(v.status, true),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl",
            class: "text-left px-3 cursor-pointer",
            renderComponent: {
                component: CollapsibleTableIcons,
                props: {
                    icons: [
                        "viewPatientEncounterIcon",
                        "viewPatientIcon",
                        "viewDoctorIcon",
                        "downloadLinkIcon",
                        "printIcon",
                        "downloadIcon",
                        "reGenerateIcon",
                        "sendToPatientIcon",
                        "goToPortalPageIcon",
                    ],
                    tableType: "Quote",
                },
            },
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = [
        "id",
        "estimateNumber",
        "expireDate",
        "createdDate",
    ];

    $: isUserAdmin = $user.role == 100;

    $: isUserEnableViewDoctor = $user.role >= 20;

    $: _getCheckboxString(selectedEstimatesIdArr);

    $: if (selectedEstimates.length >= 0 && returnEstimatesArr.length > 0) {
        selectedEstimatesIdArr = selectedEstimates.map(
            (estimate) => estimate.id,
        );
        _updateHeaderIcon();
    }

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        loadEstimates(page * pageSize, pageSize, _getSearchParams());

        if (params.query) updateCheckboxByQuery(params.query);
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (arr[i].includes("search="))
                    filters.estimateNumber = arr[i].split("=")[1].toString();

                if (arr[i].includes("id=")) filters.id = arr[i].split("=")[1];

                if (arr[i].includes("patientEncounterID="))
                    filters.patientEncounterID = arr[i]
                        .split("=")[1]
                        .toString();

                if (arr[i].includes("status="))
                    filters.status = isNaN(arr[i].split("=")[1])
                        ? arr[i].split("=")[1]
                        : Number(arr[i].split("=")[1]);

                if (arr[i].includes("doctorId=") && isUserEnableViewDoctor) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }
            }
            updateURL();
        }
    };

    const updateCheckboxByQuery = (query) => {
        const arr = query.split("&");
        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("check=")) {
                    const checkArr = arr[i].split("=")[1].split(",");
                    checkArr.map((ca) => {
                        if (
                            !selectedEstimates.some((p) => p.id == Number(ca))
                        ) {
                            let newEstimateItem = { id: Number(ca) };
                            selectedEstimates = [
                                ...selectedEstimates,
                                newEstimateItem,
                            ];
                            const checkbox = document.getElementById(
                                `selectCheckBoxId${ca}`,
                            );
                            if (checkbox) checkbox.checked = true;
                        }
                    });
                }
            }
        }
    };

    const loadEstimates = (start, limit, params) => {
        isGettingEstimates = true;
        if (params.sort) params.sort = params.sort.toLowerCase();
        if (params["filter[id]"]) {
            params = {
                "filter[id]": params["filter[id]"],
            };
        }

        apiMethods.getMethod
            .getEstimates(start, limit, params)
            .then((res) => _handleEstimates(res))
            .then(() => _updateHeaderSortIcon());
    };

    const setEstimatePaper = async () => {
        isSettingEstimatePaper = true;

        await apiMethods.putMethod
            .sendPaperEstimate(selectedEstimateID, paperProcessingFee)
            .then((res) => _handleSendPaperEstimate(res))
            .catch((err) => showException(err));

        closeSetEstimatePaperModal();

        isSettingEstimatePaper = false;
    };

    const handleFilterSelect = () => {
        page = 0;
        selectedEstimates = [];

        updateURL();

        loadEstimates(0, pageSize, _getSearchParams());
    };

    const handleSearchEstimateNo = () => {
        filters.estimateNumber = filters.estimateNumber
            ? "EST" + filters.estimateNumber.toString().replace(/est/gi, "")
            : "";

        handleFilterSelect();
    };

    const generateSelectAll = () => {
        const selectAllHeader = document.getElementsByClassName(
            "toggleSelectAllHeader",
        )[0];
        const tag = document.createElement("input");
        tag.type = "checkbox";
        tag.className =
            "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white ml-3 mt-2";
        tag.id = "toggleSelectAll";
        tag.onclick = () => {
            toggleSelectAll();
        };
        if (selectAllHeader) selectAllHeader.appendChild(tag);
    };

    const toggleSelectAll = () => {
        const selectAllCheckBox = document.getElementById("toggleSelectAll");
        if (selectAllCheckBox.className.includes("selectedAll")) {
            selectedEstimates = [];
            _updateCheckBox();
        } else {
            returnEstimatesArr.forEach((estimate) => {
                if (!selectedEstimatesIdArr.includes(estimate.id)) {
                    _addSelectedEstimate(estimate);
                }
            });
        }
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "selected?") _addSelectedEstimate(rowData);
        else if (key == "actions") {
            if (className.includes("viewDoctorIcon") && isUserEnableViewDoctor)
                goToURL(`/Account/view/${rowData.doctorId}`);
            else if (className.includes("viewPatientIcon"))
                goToURL(`/Patient/view/${rowData.patientId}`);
            else if (className.includes("viewPatientEncounterIcon"))
                if(rowData.quoteOnly){
                    goToURL(
                        `/PatientQuote/view/${rowData.patientEncounterId}/tab=feeEstimate`,
                    );
                } else {
                    goToURL(
                        `/PatientEncounter/view/${rowData.patientEncounterId}/tab=billing`,
                    );
                }
            else if (className.includes("downloadIcon"))
                downloadEstimate(rowData.id);
            else if (className.includes("reGenerateIcon"))
                regenerateEstimate(rowData);
            else if (className.includes("goToPortalPageIcon"))
                openPortalPage(rowData);
            else if (className.includes("downloadLinkIcon"))
                goToEstimate(rowData.id);
            else if (className.includes("sendToPatientIcon"))
                _openSendQuoteToPatientModal(rowData);
            else if (className.includes("printIcon"))
                openSetEstimatePaperModal(rowData);
        } else if (key == "encounterId")
            goToURL(`/PatientEncounter/view/${rowData.patientEncounterId}`);
        else if (key == "doctorName")
            goToURL(`/Account/view/${rowData.doctorId}`);
        else if (rowData.id) goToURL(`/Estimates/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (key == "selected?") _updateCheckBox();
        else if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadEstimates(0, pageSize, _getSearchParams());
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadEstimates(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
        _updateCheckBox();
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadEstimates(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        if (checkboxString)
            replaceURL(
                `/Estimates/page=${page}&size=${pageSize}&check=${checkboxString}${_getParamsString()}`,
            );
        else
            replaceURL(
                `/Estimates/page=${page}&size=${pageSize}${_getParamsString()}`,
            );
    };

    const downloadEstimate = (id, isRegenerate) => {
        goToDownloadPage(0, id, isRegenerate);
    };

    const regenerateEstimate = async (data) => {
        const { id, doctorId, patientId, patientEncounterId } = data;

        const regenerateObj = {
            type: 0,
            entityId: id,
            doctorId,
            patientId,
            patientEncounterId,
        };

        isRegeneratingEstimate = true;

        await apiMethods.postMethod
            .regeneratePdfFile(regenerateObj)
            .then((res) => {
                if (res.id) downloadEstimate(id, true);
                else
                    showError(
                        "Failed to re-generate this quote PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Failed to re-generate this quote PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isRegeneratingEstimate = false;
    };

    const openPostCSVModal = () => {
        isPostCSVModalOpen = true;
    };

    const closePostCSVModal = () => {
        isPostCSVModalOpen = false;
    };

    const openSetEstimatePaperModal = async (row) => {
        hasInternalProcessingFee = false;
        if (row.internalProcessingFee) {
            paperProcessingFee = row.internalProcessingFee;
            hasInternalProcessingFee = true;
        } else if (row.groupId) {
            if (!$allGroups.length) await cacheAllGroups();
            const group = $allGroups.find((g) => g.id == row.groupId);
            paperProcessingFee =
                group?.paperProcessingFee ??
                EmptyGroupDetails.paperProcessingFee;
        } else paperProcessingFee = EmptyGroupDetails.paperProcessingFee;
        selectedEstimateID = Number(row.id);
        isSetEstimatePaperModalOpen = true;
    };

    const closeSetEstimatePaperModal = () => {
        selectedEstimateID = Number(0);
        isSetEstimatePaperModalOpen = false;
    };

    const goToEstimate = (id) => {
        // const url = `https://portal.tcihealth.com.au/?e=${id}`;
        const url = formatPortalUrls("e", id);
        window.open(url, "_blank");
    };

    // HELPER FUNCTIONS
    const _handleEstimates = (res) => {
        isGettingEstimates = false;
        if (res.error) showError(res.error);
        else {
            returnEstimatesArr = [...res.results];
            count = res.total;
        }
        setTimeout(() => {
            generateSelectAll();
            _updateCheckBox();
        }, 1);
    };

    const _handleSendPaperEstimate = (res) => {
        if (!res || res.error)
            showError(
                "Failed to request paper copy of quote, please refresh the page and try again and contact support if the issue persists.",
            );
        else if (res.id) {
            downloadEstimate(res.id);
            loadEstimates(page * pageSize, pageSize, _getSearchParams());
        }
    };

    const _updateHeaderIcon = () => {
        const headerIcon = document.getElementById("toggleSelectAll");
        if (!headerIcon) return;
        if (selectedEstimates.length === 0) {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedNone";
            headerIcon.checked = false;
            headerIcon.style = "";
        } else if (
            returnEstimatesArr.every((estimate) =>
                selectedEstimatesIdArr.includes(estimate.id),
            )
        ) {
            headerIcon.style.cssText = "";
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedAll";
            headerIcon.checked = true;
        } else {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 appearance-none selectedPartial";
            headerIcon.checked = false;
            headerIcon.style.cssText =
                "border: solid #270053; border-width: 4px; padding: 4px; border-radius: 5px; background-color: white; cursor: pointer; margin: 0.5rem 0 0 0.75rem";
        }
    };

    const _addSelectedEstimate = (data) => {
        if (selectedEstimates.some((estimate) => estimate.id === data.id)) {
            selectedEstimates = selectedEstimates.filter(
                (estimate) => estimate.id !== data.id,
            );
        } else {
            selectedEstimates = [...selectedEstimates, data];
            const checkbox = document.getElementById(
                `selectCheckBoxId${data.id}`,
            );
            if (checkbox) checkbox.checked = true;
        }
    };

    const _updateCheckBox = () => {
        setTimeout(() => {
            returnEstimatesArr.forEach((estimate) => {
                const checkbox = document.getElementById(
                    `selectCheckBoxId${estimate.id}`,
                );
                if (checkbox) {
                    if (selectedEstimatesIdArr.includes(estimate.id)) {
                        document.getElementById(
                            `selectCheckBoxId${estimate.id}`,
                        ).checked = true;
                    } else {
                        document.getElementById(
                            `selectCheckBoxId${estimate.id}`,
                        ).checked = false;
                    }
                }
            });
            _updateHeaderIcon();
        }, 1);
    };

    const _getSearchParams = () => {
        let params = {};
        if (filters.sort) params.sort = filters.sort;
        if (filters.estimateNumber) params.search = filters.estimateNumber;

        if (filters.id) params["filter[id]"] = filters.id;

        if (filters.patientEncounterID)
            params["filter[patientEncounterID]"] = filters.patientEncounterID;

        if (filters.status) params["filter[status]"] = filters.status;

        if (filters.doctorId && isUserEnableViewDoctor)
            params["filter[doctorId]"] = filters.doctorId;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            if (params.search) str += `&search=${params.search}`;

            if (params["filter[id]"]) str += `&id=${params["filter[id]"]}`;

            if (params["filter[patientEncounterID]"])
                str += `&patientEncounterID=${params["filter[patientEncounterID]"]}`;

            if (params["filter[status]"])
                str += `&status=${params["filter[status]"]}`;

            if (params["filter[doctorId]"] && isUserEnableViewDoctor)
                str += `&doctorId=${params["filter[doctorId]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _getCheckboxString = (arr) => {
        if (arr.length) checkboxString = arr.toString();
        else checkboxString = "";

        updateURL();
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Quote ID") ||
                        (params.sort == "estimateNumber" &&
                            row.childNodes[0].data == "Quote Number") ||
                        (params.sort == "expireDate" &&
                            row.childNodes[0].data == "Due Date") ||
                        (params.sort == "createdDate" &&
                            row.childNodes[0].data == "Create Date")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "Quote ID") ||
                        (params.sort == "estimateNumberDesc" &&
                            row.childNodes[0].data == "Quote Number") ||
                        (params.sort == "expireDateDesc" &&
                            row.childNodes[0].data == "Due Date") ||
                        (params.sort == "createdDateDesc" &&
                            row.childNodes[0].data == "Create Date")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getEstimateNumber = (estimateNumber) => {
        return `${estimateNumber}`;
    };

    const _getEncounterID = (encounterId, isCsvContent = false) =>
        isCsvContent
            ? `${encounterId}`
            : `<div class="cursor-pointer hover:underline">${encounterId}</div>`;

    const _getPatientName = (billingDetails) => {
        if (billingDetails.name) return `${billingDetails.name}`;
        else return "Missing patient name...";
    };

    const _getDate = (date, isExpired, isDateOnly) => {
        if (isExpired) return "Expired";

        if (isDateOnly) return getFormatDate(date);

        return getFormatDateAndTime(date);
    };

    const _getQuoteApproveDate = (date, isApproved) => {
        if (isApproved) return getFormatDate(date);

        return "Not approved";
    };

    const _getStatus = (status, isCsvContent = false) => {
        let statusText, statusColor;

        statusText = getEstimateStatus(status);

        if (isCsvContent) return statusText;

        switch (status) {
            case 1:
            case 2:
            case 4:
                statusColor = "#2DBD6F";
                break;
            case 5:
            case 9:
                statusColor = "#E05757";
                break;
            default:
                statusColor = "";
        }

        return `
                <div class="flex justify-start items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const _openSendQuoteToPatientModal = async (data) => {
        const { billingData, patient } =
            await apiMethods.getMethod.getPatientEncounterById(
                data.patientEncounterId,
            );

        if (!billingData) {
            showError(
                "Failed to get quote related insurance type, please refresh the page and try again and contact support if the issue persists.",
            );
            return;
        }

        data.insuranceType = billingData.find(
            (bd) => bd.isLatestQuoteData,
        )?.insuranceType;

        sendQuoteToPatientModalOpen = true;
        selectedQuoteData = {
            ...data,
            type: "quote",
            isFromTable: true,
            patientContacts: patient?.patientContacts ?? [],
        };
    };

    const _closeSendQuoteToPatientModal = () => {
        sendQuoteToPatientModalOpen = false;
        selectedQuoteData = {};
    };

    let paperProcessingFee,
        hasInternalProcessingFee = false;

    $: isValidPaperProcessingFee =
        paperProcessingFee >= 0 && paperProcessingFee <= MaxPaperProcessingFee;
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
            on:click={toggleFilters}
        >
            Filters
            <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
            <i
                style="font-size:14px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}
    {#if showFilters || !$isMobile}
        <div
            class="flex flex-0 items-start {$isMobile
                ? 'flex-col-reverse gap-y-4'
                : 'justify-between'}"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <div
                class="flex flex-row flex-1 items-end justify-start flex-wrap gap-4"
            >
                <SearchInput
                    bind:value={filters.id}
                    title="Search Quote ID"
                    inputFunc={handleFilterSelect}
                />

                <SearchInput
                    bind:value={filters.estimateNumber}
                    title="Search Quote No."
                    placeholder="EST"
                    inputFunc={handleSearchEstimateNo}
                />

                {#if isUserEnableViewDoctor}
                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32"
                        zIndex="3"
                    />
                {/if}

                <SearchInput
                    bind:value={filters.patientEncounterID}
                    title="Patient Encounter ID"
                    type="number"
                    inputFunc={handleFilterSelect}
                />

                <SearchSelect
                    bind:value={filters.status}
                    title="Status"
                    onChangeFunc={handleFilterSelect}
                >
                    <option value="1,2,4,5" selected>
                        All except cancelled
                    </option>
                    {#each estimateStatus as { id, type }}
                        <option value={id} selected={id == filters.status}>
                            {type}
                        </option>
                    {/each}
                </SearchSelect>

                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={clearFilterDoctor}
                />
            </div>

            {#if isUserAdmin}
                <Button clickFunc={openPostCSVModal}>
                    Upload EFT Payments CSV
                </Button>
            {/if}
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingEstimates}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
                id="estimateTableContainer"
            >
                <SvelteTable
                    tableContainerId="estimateTableContainer"
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnEstimatesArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        showSelected
        selectedCount={selectedEstimates.length}
        tableData={returnEstimatesArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: returnEstimatesArr,
            fileName: "Quotes",
            allDataApi: async () =>
                await apiMethods.getMethod.getEstimates(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />

    <PostCsvFileModal {isPostCSVModalOpen} closeModal={closePostCSVModal} />
</div>

{#if isDownloadingEstimate}
    <LoadingModal titleText="Downloading Quote PDF..." />
{/if}

{#if isRegeneratingEstimate}
    <LoadingModal titleText="Regenerating Quote PDF..." />
{/if}

{#if isSendingEstimateToPatient}
    <LoadingModal titleText="Sending Quote to Patient..." />
{/if}

<NotificationModal
    isModalOpen={isSetEstimatePaperModalOpen}
    title="Get paper quote"
    cancelText="Cancel"
    submitText={isSettingEstimatePaper ? "Confirming" : "Confirm"}
    closeModal={closeSetEstimatePaperModal}
    confirmAction={setEstimatePaper}
    isConfirming={isSettingEstimatePaper}
    closeModalFunction={closeSetEstimatePaperModal}
    isConfirmDisabled={!isValidPaperProcessingFee}
    modalMaxWidth="max-w-xl"
>
    <div class="flex gap-x-4 items-center">
        {#if hasInternalProcessingFee}
            Change the processing fee to
        {:else}
            Add
        {/if}
        $
        <DecimalInput
            bind:value={paperProcessingFee}
            inputMax={MaxPaperProcessingFee}
            inputClass={isValidPaperProcessingFee ? "w-16" : "input-error w-16"}
        />
        {#if !hasInternalProcessingFee}
            processing fee to the quote and download.
        {/if}
    </div>
    {#if paperProcessingFee > MaxPaperProcessingFee}
        <p class="text-error mt-5">
            The maximum allowed paper processing fee is ${MaxPaperProcessingFee}.
        </p>
    {/if}
</NotificationModal>

<SendQuoteOrInvoiceToPatientModal
    data={selectedQuoteData}
    paymentsData={selectedQuoteData?.payments}
    bind:isModalOpen={sendQuoteToPatientModalOpen}
    afterSendFunc={_closeSendQuoteToPatientModal}
/>
